import {testIdProps} from '@github-ui/test-id-props'
import type {ResponsiveValue} from '@primer/react'
import {clsx} from 'clsx'
import {type CSSProperties, type PropsWithChildren, type ReactNode, useEffect} from 'react'

import {useListViewVariant} from '../ListView/VariantContext'
import {useListItemAlignment} from './AlignmentContext'
import styles from './LeadingVisual.module.css'
import {useListItemNewActivity} from './NewActivityContext'
import {useListItemStatus} from './StatusContext'
import {ListItemUnreadIndicator} from './UnreadIndicator'

const DEFAULT_VISUAL_SIZE = 16

type LeadingVisualStyle = CSSProperties & {
  '--leadingVisual-size-default': string
  '--leadingVisual-size-narrow': string
  '--leadingVisual-size-regular': string
  '--leadingVisual-size-wide': string
  '--leadingVisual-marginTop-default': string
  '--leadingVisual-marginTop-narrow': string
  '--leadingVisual-marginTop-regular': string
  '--leadingVisual-marginTop-wide': string
}

function resolveResponsiveSize(sizeProp: number | ResponsiveValue<number>, viewport: keyof ResponsiveValue<number>) {
  return typeof sizeProp === 'number' ? sizeProp : (sizeProp[viewport] ?? DEFAULT_VISUAL_SIZE)
}

export type ListItemLeadingVisualProps = {
  /**
   * The Octicon component representing the desired icon
   */
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>
  /**
   * Render any child content as the leading visual
   * NOTE: if both icon and children are passed, the icon will be rendered first, with the children appended after
   */
  children?: ReactNode
  /**
   * Additional className prop to override or extend pre-existing className styles
   */
  className?: string
  /**
   * CSS color value to apply to the leading visual
   */
  color?: string
  /**
   * A text description of the item
   * Used for visually hidden text to describe the item on focus for the screen readers
   */
  description?: string
  /**
   * Indicates whether the rendered item has new content since lastViewed
   */
  newActivity?: boolean
  /**
   * Size of the leading visual in pixels (width and height). Default is 16px.
   */
  size?: number | ResponsiveValue<number>
}

export function ListItemLeadingVisual({
  icon: Icon,
  color,
  description,
  newActivity = false,
  className,
  children,
  size: sizeProp = DEFAULT_VISUAL_SIZE,
  ...props
}: ListItemLeadingVisualProps & PropsWithChildren) {
  const {variant} = useListViewVariant()
  const {setStatus} = useListItemStatus()
  const {setHasNewActivity} = useListItemNewActivity()
  const trimmedDescription = description?.trim()
  const {alignment} = useListItemAlignment()
  const defaultSize = typeof sizeProp === 'number' ? sizeProp : DEFAULT_VISUAL_SIZE
  const narrowSize = resolveResponsiveSize(sizeProp, 'narrow')
  const regularSize = resolveResponsiveSize(sizeProp, 'regular')
  const wideSize = typeof sizeProp === 'number' ? sizeProp : (sizeProp.wide ?? sizeProp.regular ?? DEFAULT_VISUAL_SIZE)
  const marginTopOffset = variant === 'compact' ? 18 : newActivity ? 13 : 22
  const getMarginTop = (size: number) => (alignment === 'center' ? 0 : marginTopOffset - size / 2)
  const responsiveStyle = {
    '--leadingVisual-size-default': `${defaultSize}px`,
    '--leadingVisual-size-narrow': `${narrowSize}px`,
    '--leadingVisual-size-regular': `${regularSize}px`,
    '--leadingVisual-size-wide': `${wideSize}px`,
    '--leadingVisual-marginTop-default': `${getMarginTop(defaultSize)}px`,
    '--leadingVisual-marginTop-narrow': `${getMarginTop(narrowSize)}px`,
    '--leadingVisual-marginTop-regular': `${getMarginTop(regularSize)}px`,
    '--leadingVisual-marginTop-wide': `${getMarginTop(wideSize)}px`,
  } satisfies LeadingVisualStyle

  useEffect(() => {
    setHasNewActivity(newActivity)
  }, [newActivity, setHasNewActivity])

  useEffect(() => {
    if (trimmedDescription) setStatus(trimmedDescription)
  }, [setStatus, trimmedDescription])

  return (
    <div className={clsx(styles.outer, className)} style={responsiveStyle} {...props}>
      <div>
        <div {...testIdProps('list-view-leading-visual')} className={styles.inner}>
          {Icon && (
            <span className="d-flex" style={{color}}>
              <Icon aria-hidden="true" />
            </span>
          )}
          {children}
          {trimmedDescription && (
            <span className="sr-only" {...testIdProps('leading-visual-text-description')}>
              {trimmedDescription}
            </span>
          )}
        </div>
      </div>
      {newActivity && <ListItemUnreadIndicator />}
    </div>
  )
}
